import React, { useState, useEffect, useCallback } from 'react';
import authService, { AuthenticationResultStatus, AuthenticationResult } from './AuthorizeService';
import { LoginActions, QueryParameterNames, ApplicationPaths } from './ApiAuthorizationConstants';
import LoginFormComponent from '../auth/LoginFormComponent';
import { Box, CircularProgress, Typography } from '@mui/material';

interface LoginProps {
    action: string;
}

export const Login: React.FC<LoginProps> = ({ action }) => {
    const [message, setMessage] = useState<string | undefined>(undefined);

    const getReturnUrl = useCallback((state?: { returnUrl: string }): string => {
        const params = new URLSearchParams(window.location.search);
        let fromQuery = params.get(QueryParameterNames.ReturnUrl);

        if (fromQuery) {
            try {
                const url = new URL(fromQuery, window.location.origin);
                if (url.origin !== window.location.origin) {
                    fromQuery = null;
                }
            } catch (e) {
                fromQuery = null;
            }
        }
        return (state && state.returnUrl) || fromQuery || `${window.location.origin}/`;
    }, []);

    const navigateToReturnUrl = useCallback((returnUrl: string) => {
        window.location.replace(returnUrl);
    }, []);

    const processLoginCallback = useCallback(async () => {
        const url: string = window.location.href;
        const result: AuthenticationResult = await authService.completeSignIn(url);
        switch (result.status) {
            case AuthenticationResultStatus.Redirect:
                throw new Error('Перенаправление не должно происходить.');
            case AuthenticationResultStatus.Success:
                await navigateToReturnUrl(getReturnUrl(result.state));
                break;
            case AuthenticationResultStatus.Fail:
                setMessage(result.message);
                break;
            default:
                throw new Error(`Недопустимый статус результата аутентификации '${result.status}'.`);
        }
    }, [getReturnUrl, navigateToReturnUrl]);

    const redirectToApiAuthorizationPath = useCallback((apiAuthorizationPath: string) => {
        const redirectUrl = `${window.location.origin}/${apiAuthorizationPath}`;
        window.location.replace(redirectUrl);
    }, []);

    const redirectToRegister = useCallback(() => {
        redirectToApiAuthorizationPath(`${ApplicationPaths.IdentityRegisterPath}?${QueryParameterNames.ReturnUrl}=${encodeURI(ApplicationPaths.Login)}`);
    }, [redirectToApiAuthorizationPath]);

    const redirectToProfile = useCallback(() => {
        redirectToApiAuthorizationPath(ApplicationPaths.IdentityManagePath);
    }, [redirectToApiAuthorizationPath]);

    useEffect(() => {
        const processAction = async () => {
            switch (action) {
                case LoginActions.Login:
                    // Render form, no async action needed here
                    break;
                case LoginActions.LoginCallback:
                    await processLoginCallback();
                    break;
                case LoginActions.LoginFailed:
                    const params = new URLSearchParams(window.location.search);
                    const error = params.get(QueryParameterNames.Message);
                    setMessage(error || undefined);
                    break;
                case LoginActions.Profile:
                    redirectToProfile();
                    break;
                case LoginActions.Register:
                    redirectToRegister();
                    break;
                default:
                    throw new Error(`Недопустимое действие '${action}'`);
            }
        };
        processAction();
    }, [action, processLoginCallback, redirectToProfile, redirectToRegister]);

    if (message) {
        return <Typography color="error">{message}</Typography>;
    }

    switch (action) {
        case LoginActions.Login:
            return <LoginFormComponent />;
        case LoginActions.LoginCallback:
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Обработка ответа входа...</Typography>
                </Box>
            );
        case LoginActions.Profile:
        case LoginActions.Register:
            return <Box />; // Empty box, redirection is handled in useEffect
        default:
            throw new Error(`Недопустимое действие '${action}'`);
    }
};
